import { Button, Form, TextField, Checkbox, Image } from '@adobe/react-spectrum';
import { ToastQueue } from '@adobe/react-spectrum';
import { KeyboardEvent, useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDependency } from '../../contexts/DependencyProvider';
import { doLogin } from '../../utils/LoginUtils';
import { useViewInfo } from '../../hooks/UseViewInfo';
import { useReefConfig } from '../../hooks/UseReefConfig';
import { WhiteLabelTheme } from '../../config/white-label-config';
import styles from './WhiteLabelLogin.module.css';

const md5 = require('md5');

interface WhiteLabelLoginProps {
  theme: WhiteLabelTheme;
}

const WhiteLabelLogin = ({ theme }: WhiteLabelLoginProps) => {
  const navigate = useNavigate();
  const { loginInfo } = useReefConfig();
  const { isDesktop } = useViewInfo();

  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [server, setServer] = useState<string>(loginInfo.serverName);
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const { mainService, store } = useDependency();
  const is_login_in_progress = useRef(false);

  const onHandleKeyUp = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleLogin();
    }
  };

  useEffect(() => {
    setServer(loginInfo.serverName);
  }, [loginInfo]);

  const processLogin = useCallback(async (uname: string, pass: string, serv: string, plainPassword: boolean = true) => {
    try {
      if (is_login_in_progress.current) return;
      is_login_in_progress.current = true;
      setIsDisabled(true);

      var p_username: string = uname;
      if (uname !== 'root' && !uname.includes('@')) {
        if (loginInfo.instanceName !== '' && loginInfo.instanceName !== null) p_username += '@' + loginInfo.instanceName;
      }
      const { status, message, session } = await doLogin(p_username, plainPassword ? md5(pass) : pass, serv, mainService, store);
      is_login_in_progress.current = false;
      if (status) {
        if (rememberMe) {
          store.Password = plainPassword ? md5(pass) : pass;
        }
        store.Username = p_username;
        store.Server = serv;
        store.UserId = +session!.userId;
        store.session = session!;

        if (isDesktop) ToastQueue.positive('Login is done!', { timeout: 3000 });

        navigate('/user/dashboard', { state: { session } });
      } else {
        ToastQueue.negative(message, { timeout: 3000 });
      }
    } catch (error) {
      console.log('error in handleLogin', error);
      is_login_in_progress.current = false;
    } finally {
      setIsDisabled(false);
    }
  }, [loginInfo.instanceName, mainService, store, navigate, isDesktop, rememberMe]);

  const handleLogin = async () => {
    if (username === '' || password === '' || server === '') {
      ToastQueue.negative('Please fill in all fields!', { timeout: 3000 });
      return;
    }

    await processLogin(username, password, server);
  };

  useEffect(() => {
    if (store.Password) {
      setUsername(store.Username);
      setPassword(store.Password);
      setServer(store.Server);
      setRememberMe(true);
      processLogin(store.Username, store.Password, store.Server, false);
    }
  }, [store, processLogin]);

  // Apply theme colors as CSS custom properties
  const themeStyles = {
    '--background-color': theme.backgroundColor,
    '--background-image': `url(${theme.backgroundImage})`,
    '--dialog-background-color': theme.dialogBackgroundColor,
    '--base-color': theme.baseColor,
    '--base-color-dark': adjustColorBrightness(theme.baseColor, -20),
  } as React.CSSProperties;

  return (
    <div 
      className={`${styles.whiteLabelLoginContainer} ${styles.overrideLayoutBackground}`}
      style={{
        ...themeStyles,
        backgroundColor: theme.backgroundColor
      }}
    >
      <div className={styles.mainContent}>
        {/* Login Section */}
        <div className={styles.loginSection}>
          <div className={styles.loginDialog}>
            {/* Title Logo */}
            <div className={styles.titleLogo}>
              <Image src={theme.titleLogo} alt={`${theme.productName} Logo`} />
            </div>

            {/* Welcome Text */}
            <div className={styles.welcomeText}>
              Welcome back!
            </div>

            {/* Login Form */}
            <Form maxWidth="100%">
              <div className={styles.formContainer}>
                <TextField 
                  label="Username" 
                  isRequired 
                  value={username} 
                  onChange={setUsername} 
                  isDisabled={isDisabled} 
                  onKeyUp={onHandleKeyUp} 
                  width="100%"
                />

                <TextField 
                  label="Password" 
                  type="password" 
                  isRequired 
                  value={password} 
                  onChange={setPassword} 
                  isDisabled={isDisabled} 
                  onKeyUp={onHandleKeyUp} 
                  width="100%"
                />

                <TextField 
                  label="Server" 
                  isRequired 
                  value={server} 
                  onChange={setServer} 
                  isDisabled={isDisabled} 
                  onKeyUp={onHandleKeyUp} 
                  isHidden={!loginInfo.showServer} 
                  width="100%"
                />

                <Checkbox isDisabled={isDisabled} isSelected={rememberMe} onChange={setRememberMe}>
                  Remember me
                </Checkbox>
              </div>

              <div className={styles.forgotPassword}>
                Forgot password? Please ask your administrator.
              </div>

              <Button 
                variant="cta" 
                onPress={handleLogin} 
                isDisabled={isDisabled}
                width="100%"
                UNSAFE_className={styles.loginButton}
              >
                Login
              </Button>
            </Form>
          </div>
        </div>

        {/* Logo Section */}
        <div className={styles.logoSection}>
          <div className={styles.mainLogo}>
            <Image src={theme.mainLogo} alt={`${theme.productName} Main Logo`} />
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Helper function to adjust color brightness
 */
function adjustColorBrightness(color: string, amount: number): string {
  // Simple implementation - for production, consider using a proper color manipulation library
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  const newR = Math.max(0, Math.min(255, r + amount));
  const newG = Math.max(0, Math.min(255, g + amount));
  const newB = Math.max(0, Math.min(255, b + amount));
  
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
}

export default WhiteLabelLogin;
